import React, { ReactElement, useState, useMemo, useContext } from "react";
import {
  Button,
  makeStyles,
  Modal,
  TextField,
  MenuItem,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import ChamberMemberSelector from "../ChamberMemberAdmin/ChamberMemberSelector";
import { useLocation, useHistory } from "react-router-dom";
import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import WarningIcon from "@material-ui/icons/Warning";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import TrashIcon from "../../../images/trash.png";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import SuccessDialog from "../../common/SuccessDialog";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import omitDeep from "omit-deep-lodash";
import BannerUploadModal from "./BannerUploadModal";
import BannerPreviewModal from "./BannerPreviewModal";
import PerksBannerPhoneMockup from "../../../images/perksbanner.jpeg";
import DeleteBannerModal from "./DeleteBannerModal";
import {
  UserType,
  BannerContentTypeEnum,
  useGetAllChamberMembersQuery,
  useUpdateChamberMemberMutation,
  useGetAllDiscountsFromChamberMemberIdQuery,
  useUpdateDiscountMutation,
} from "../../../graphql/types";
import { UserTypeContext } from "../../../UserTypeContext";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import FeaturedMemberBannerGuidance from "../../common/FeaturedMemberBannerGuidance";

const useStyles = makeStyles((themes: any) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
    overflowX: "hidden",
  },
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    width: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  title: {
    fontWeight: "bold",
    fontSize: "32px",
    color: "white",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
    },
  },
  headerIcons: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },
  headerIcon: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  description: {
    fontSize: "18px",
    color: theme.palette.primary.contrastText,
    margin: "0 0 16px 0",
    lineHeight: "1.5",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  videoButton: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.dark,
    fontWeight: "bold",
    fontSize: "14px",
    textTransform: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.background.default,
    },
  },
  pageHeaderButtonRow: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  contentContainer: {
    backgroundColor: theme.palette.primary.main,
    padding: "0 40px 40px 40px",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "0 0 20px 0",
    },
  },
  content: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "16px",
    padding: "30px",
    minHeight: "70vh",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
      borderRadius: 0,
    },
  },
  dashedContainer: {
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: "16px",
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 0",
      border: "none",
      borderRadius: 0,
    },
  },
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "16px",
    padding: "40px",
    maxWidth: "500px",
    width: "90%",
    position: "relative",
    textAlign: "center",
  },
  closeButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
    cursor: "pointer",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: theme.palette.delete.main,
    color: theme.palette.delete.contrastText,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    fontSize: "18px",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.grey[300],
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.grey[300],
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.common.black,
      },
    },
  },
}));

const AddMemberBanners = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const location = useLocation();
  const history = useHistory();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const {
    state: { userType },
  } = useContext(UserTypeContext) as any;
  const [activeBannerType, setActiveBannerType] = useState<"PROFILE" | "PERK">(
    "PROFILE"
  );
  const [activeTab, setActiveTab] = useState<"MANAGE" | "INSTRUCTIONS">("MANAGE");

  const [selectedMember, setSelectedMember] = useState<string>("");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number>(-1);
  const [pendingDeleteType, setPendingDeleteType] = useState<"PROFILE" | "PERK">("PROFILE");

  // Preview Modal State
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewPerkModalOpen, setPreviewPerkModalOpen] = useState(false);

  // Banner Modal State
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [bannersData, setBannersData] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
  ]);

  const [selectedPerkId, setSelectedPerkId] = useState<string>("");
  const [perkBannersData, setPerkBannersData] = useState<
    Array<{
      image: string | null;
      videoUrl: string | null;
      redirectUrl: string | null;
    }>
  >([
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
    { image: null, videoUrl: null, redirectUrl: null },
  ]);

  const [dialogState, setDialogState] = useState<{ open: boolean; message: string; variant: "success" | "error" }>({ open: false, message: "", variant: "success" });

  const [updateChamberMember] = useUpdateChamberMemberMutation();
  const [updateDiscount] = useUpdateDiscountMutation();

  const chamberIdToUse = useMemo(() => {
    if (userType === UserType.RootAdmin) {
      return (
        (location.state || { chamberId: undefined }) as {
          chamberId: string | undefined;
        }
      ).chamberId;
    }
    return chamberInfo?.id;
  }, [chamberInfo?.id, location?.state, userType]);

  const { data: allChamberMemberData } = useGetAllChamberMembersQuery({
    variables: { chamberId: chamberIdToUse || "" },
    skip: !chamberIdToUse,
    fetchPolicy: "cache-and-network",
  });

  const { data: memberPerksData } = useGetAllDiscountsFromChamberMemberIdQuery({
    variables: { chamberMemberId: selectedMember || "" },
    skip: !selectedMember,
    fetchPolicy: "cache-and-network",
  });

  const allChamberMembers: any[] = useMemo(() => {
    return allChamberMemberData?.getAllChamberMembers || [];
  }, [allChamberMemberData]);

  const featuredChamberMembers: any[] = useMemo(() => {
    return allChamberMembers.filter((member) =>
      !!member.activeMemberProfileAndPerksBannersSettings?.activationInfo
    );
  }, [allChamberMembers]);

  const isFeaturedMember = useMemo(() => {
    if (!selectedMember) return false;
    const member = allChamberMembers.find((m) => m.id === selectedMember);
    const activationInfo = member?.activeMemberProfileAndPerksBannersSettings?.activationInfo;
    return !!activationInfo?.startDateUtcMilli;
  }, [selectedMember, allChamberMembers]);

  const memberPerks = memberPerksData?.getAllDiscountsFromChamberMemberId || [];
  const hasPerks = memberPerks.length > 0;

  // Sync banners when member is selected
  React.useEffect(() => {
    if (selectedMember) {
      const member = allChamberMembers.find((m) => m.id === selectedMember);
      if (member) {
        const newBannersData = [
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
        ];
        (member.banner || []).forEach((b: any, idx: number) => {
          if (idx < 4) {
            if (b.contentType === "IMAGE" && b.contentUrl) {
              newBannersData[idx] = {
                image: b.contentUrl,
                videoUrl: null,
                redirectUrl: b.imageLink || null,
              };
            } else if (b.contentType === "VIDEO" && b.contentUrl) {
              newBannersData[idx] = {
                image: null,
                videoUrl: b.contentUrl,
                redirectUrl: b.imageLink || null,
              };
            }
          }
        });
        setBannersData(newBannersData);
      }
    } else {
      setBannersData([
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
      ]);
    }
  }, [selectedMember, allChamberMembers]);

  // Auto-select first perk when featured member has perks
  React.useEffect(() => {
    if (isFeaturedMember && memberPerks.length > 0 && !selectedPerkId) {
      setSelectedPerkId(memberPerks[0].id);
    }
  }, [isFeaturedMember, memberPerks, selectedPerkId]);

  // Sync perk banners when perk is selected
  React.useEffect(() => {
    if (selectedPerkId && memberPerksData?.getAllDiscountsFromChamberMemberId) {
      const perk = memberPerksData.getAllDiscountsFromChamberMemberId.find(
        (p) => p.id === selectedPerkId
      );
      if (perk) {
        const newBannersData = [
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
          { image: null, videoUrl: null, redirectUrl: null },
        ];
        ((perk as any).banner || []).forEach((b: any, idx: number) => {
          if (idx < 4) {
            if (b.contentType === "IMAGE" && b.contentUrl) {
              newBannersData[idx] = {
                image: b.contentUrl,
                videoUrl: null,
                redirectUrl: b.imageLink || null,
              };
            } else if (b.contentType === "VIDEO" && b.contentUrl) {
              newBannersData[idx] = {
                image: null,
                videoUrl: b.contentUrl,
                redirectUrl: b.imageLink || null,
              };
            }
          }
        });
        setPerkBannersData(newBannersData);
      }
    } else {
      setPerkBannersData([
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
        { image: null, videoUrl: null, redirectUrl: null },
      ]);
    }
  }, [selectedPerkId, memberPerksData]);

  const handleMoveBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...bannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setBannersData(newBannersData);
    }
  };

  const handleDeleteBanner = (index: number) => {
    setPendingDeleteIndex(index);
    setPendingDeleteType("PROFILE");
    setDeleteModalOpen(true);
  };

  const handleMovePerkBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...perkBannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setPerkBannersData(newBannersData);
    }
  };

  const handleDeletePerkBanner = (index: number) => {
    setPendingDeleteIndex(index);
    setPendingDeleteType("PERK");
    setDeleteModalOpen(true);
  };

  const confirmDeleteBanner = () => {
    if (pendingDeleteType === "PROFILE") {
      const newBannersData = [...bannersData];
      newBannersData[pendingDeleteIndex] = { image: null, videoUrl: null, redirectUrl: null };
      setBannersData(newBannersData);
    } else {
      const newBannersData = [...perkBannersData];
      newBannersData[pendingDeleteIndex] = { image: null, videoUrl: null, redirectUrl: null };
      setPerkBannersData(newBannersData);
    }
    setDeleteModalOpen(false);
    setPendingDeleteIndex(-1);
  };

  const handleOpenBannerModal = (index: number, type: "PROFILE" | "PERK") => {
    setActiveBannerType(type);
    setCurrentBannerIndex(index);
    setBannerModalOpen(true);
  };

  const handleSaveBanner = async (data: {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  }) => {
    if (activeBannerType === "PROFILE") {
      await handleSaveProfileBanner(data);
    } else {
      await handleSavePerkBanner(data);
    }
  };

  const handleSaveProfileBanner = (data: {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  }) => {
    if (!selectedMember) return;

    const newBannersData = [...bannersData];
    newBannersData[currentBannerIndex - 1] = data;
    setBannersData(newBannersData);
    setBannerModalOpen(false);
  };

  const handleSavePerkBanner = (data: {
    image: string | null;
    videoUrl: string | null;
    redirectUrl: string | null;
  }) => {
    if (!selectedPerkId) return;

    const newBannersData = [...perkBannersData];
    newBannersData[currentBannerIndex - 1] = data;
    setPerkBannersData(newBannersData);
    setBannerModalOpen(false);
  };

  const handleUpdateAllProfileBanners = async () => {
    if (!selectedMember) return;
    try {
      setLoader(true);
      const member = allChamberMembers.find((m: any) => m.id === selectedMember);
      if (!member) return;

      const bannersToSave = bannersData
        .map((b, i) => {
          if (b.image) {
            return {
              contentUrl: b.image,
              imageLink: b.redirectUrl || "",
              contentType: BannerContentTypeEnum.Image,
              position: i,
            };
          } else if (b.videoUrl) {
            return {
              contentUrl: b.videoUrl,
              imageLink: b.redirectUrl || "",
              contentType: BannerContentTypeEnum.Video,
              position: i,
            };
          }
          return null;
        })
        .filter((b): b is NonNullable<typeof b> => b !== null);

      const mutationInput = omitDeep({
            id: selectedMember,
            address: {
              city: member.address.city,
              postalCode: member.address.postalCode,
              province: member.address.province,
              streetAddress: member.address.streetAddress,
            },
            categoryId: member.categoryId,
            chamberId: member.chamberId,
            description: member.description,
            contactName: member.contactName,
            email: member.email,
            phoneNumber: member.phoneNumber,
            showInApp: member.showInApp,
            name: member.name,
            website: member.website,
            facebook: member.facebook,
            instagram: member.instagram,
            threads: member.threads,
            linkedin: member.linkedin,
            twitter: member.twitter,
            tiktok: member.tiktok,
            youtube: member.youtube,
            googleReviewLink: member.googleReviewLink,
            logo: member.logo,
            customContacts: member.customContacts?.map((c: any) => ({
              id: c.id,
              name: c.name,
              email: c.email,
              jobTitle: c.jobTitle,
              phoneNumber: c.phoneNumber,
            })) || [],
            adminConsoleContactName: member.adminConsoleContactName,
            isSuspended: member.isSuspended,
            banner: bannersToSave,
      }, "__typename");

      await updateChamberMember({
        variables: {
          id: selectedMember,
          input: mutationInput,
        },
        refetchQueries: ["getAllChamberMembers"],
        awaitRefetchQueries: true,
      });
      setDialogState({ open: true, message: enumStrings.toastMessages.profileBannersUpdated, variant: "success" });
    } catch (error) {
      console.error(error);
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToUpdateProfileBanners, variant: "error" });
    } finally {
      setLoader(false);
    }
  };

  const handleUpdateAllPerkBanners = async () => {
    if (!selectedPerkId) return;
    try {
      setLoader(true);
      const perk = memberPerksData?.getAllDiscountsFromChamberMemberId?.find(
        (p: any) => p.id === selectedPerkId
      );
      if (!perk) return;

      const bannersToSave = perkBannersData
        .map((b, i) => {
          if (b.image) {
            return {
              contentUrl: b.image,
              imageLink: b.redirectUrl || "",
              contentType: BannerContentTypeEnum.Image,
              position: i,
            };
          } else if (b.videoUrl) {
            return {
              contentUrl: b.videoUrl,
              imageLink: b.redirectUrl || "",
              contentType: BannerContentTypeEnum.Video,
              position: i,
            };
          }
          return null;
        })
        .filter((b): b is NonNullable<typeof b> => b !== null);

      const perkInput = omitDeep({
        id: selectedPerkId,
        title: (perk as any).title,
        description: (perk as any).description,
        chamberId: (perk as any).chamberId,
        chamberMemberId: (perk as any).chamberMemberId,
        memberOnly: (perk as any).memberOnly,
        isAvailableToAllMembers: (perk as any).isAvailableToAllMembers,
        isReoccuring: (perk as any).isReoccuring,
        isPrivate: (perk as any).isPrivate,
        privateEmails: (perk as any).privateEmails,
        daysAvailable: (perk as any).daysAvailable,
        startTimeUtcMilli: (perk as any).startTimeUtcMilli,
        expiryTimeUtcMilli: (perk as any).expiryTimeUtcMilli,
        redeemSettings: (perk as any).redeemSettings,
        notify: false,
        banner: bannersToSave,
      }, "__typename");

      await updateDiscount({
        variables: {
          id: selectedPerkId,
          input: perkInput,
        },
        refetchQueries: ["getAllDiscountsFromChamberMemberId"],
        awaitRefetchQueries: true,
      });
      setDialogState({ open: true, message: enumStrings.toastMessages.perkBannersUpdated, variant: "success" });
    } catch (error) {
      console.error(error);
      setDialogState({ open: true, message: enumStrings.toastMessages.failedToUpdatePerkBanners, variant: "error" });
    } finally {
      setLoader(false);
    }
  };

  const handleBannerSectionClick = () => {
    if (!selectedMember) {
      setShowWarningModal(true);
    }
  };

  return (
    <div className={classes.root}>
      {/* Header Section */}
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <h1 className={classes.title}>{enumStrings.addMemberBanners.title}</h1>
        </div>
        <p className={classes.description}>
          {enumStrings.addMemberBanners.description}
        </p>
        <div
          style={{ display: "flex", gap: isMobile ? "8px" : "12px", alignItems: isMobile ? "stretch" : "center", flexDirection: isMobile ? "column" : "row", flexWrap: "wrap" }}
          className={classes.pageHeaderButtonRow}
        >
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/5bf66689-ccd5-4b6d-8f49-d05f809cef74"
          />
          {userType !== UserType.RootAdmin && (
            <ExportButton
              customOnClick={() => openInstructionPdf("Add and manage member Feature Member Banners.pdf")}
              disablePadding
              icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
              label={enumStrings.manageChamberEngage.viewInstructions}
            />
          )}
        </div>
      </div>

      {/* Content Container */}
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.dashedContainer}>
            {/* Choose Member Dropdown */}
            <div style={{ width: "100%", maxWidth: "700px", marginBottom: "30px" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                <label
                  style={{
                    fontSize: isMobile ? "15px" : "18px",
                    fontWeight: 600,
                    color: "#000000",
                  }}
                >
                  {enumStrings.addMemberBanners.chooseMember}
                  <span style={{ color: theme.palette.error.main, marginLeft: "4px" }}>*</span>
                </label>
              </div>
              <p
                style={{
                  fontSize: isMobile ? "13px" : "18px",
                  lineHeight: isMobile ? "20px" : undefined,
                  fontWeight: 400,
                  color: "#000000",
                  margin: "0 0 12px 0",
                }}
              >
                {enumStrings.addMemberBanners.chooseMemberDesc}
              </p>
              <ChamberMemberSelector
                chamberMemberId={selectedMember || null}
                setChamberMemberId={setSelectedMember}
                allChamberMemberData={{ getAllChamberMembers: featuredChamberMembers } as any}
                fullWidth
                hideTooltip
                hideLabel
              />
            </div>

            {/* Featured Member Profile Banners Section */}
            {selectedMember && (
              <>
              <div
                style={{
                  width: "100%",
                  maxWidth: "1096px",
                  margin: isMobile ? "24px auto 0 auto" : "40px auto 0 auto",
                  padding: isMobile ? "0 0 20px 0" : "0px 30px 30px 30px",
                  border: isMobile ? "none" : `1px solid ${theme.palette.text.primary}`,
                  borderRadius: isMobile ? 0 : "16px",
                  position: "relative",
                  boxSizing: "border-box",
                }}
                onClick={handleBannerSectionClick}
              >
                {/* Green/Black Pill Header */}
                <div
                  style={{
                    backgroundColor: isFeaturedMember ? theme.palette.success.main : "#000000",
                    borderRadius: "30px",
                    padding: isMobile ? "8px 14px" : "12px 32px",
                    display: "inline-block",
                    position: "relative",
                    top: isMobile ? "0" : "-24px",
                    left: "0",
                    marginBottom: isMobile ? "16px" : 0,
                    maxWidth: isMobile ? "calc(100% - 8px)" : undefined,
                  }}
                >
                  <p
                    style={{
                      color: "#FFFFFF",
                      fontSize: isMobile ? "13px" : "20px",
                      fontWeight: 700,
                      margin: 0,
                      lineHeight: isMobile ? "16px" : "24px",
                    }}
                  >
                    {isFeaturedMember
                      ? enumStrings.editMemberForm.featuredMemberActivatedTitle
                      : enumStrings.editMemberForm.featuredMemberNotActivatedTitle}
                  </p>
                </div>

                {/* Description */}
                {isFeaturedMember ? (
                  <p
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: isMobile ? "14px" : "18px",
                      lineHeight: isMobile ? "20px" : "28px",
                      color: "#000000",
                      textAlign: "left",
                      marginBottom: "16px",
                    }}
                    dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.featuredMemberUpgradedDesc }}
                  />
                ) : (
                  <p
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: isMobile ? "14px" : "18px",
                      lineHeight: isMobile ? "20px" : "28px",
                      color: "#000000",
                      fontWeight: 400,
                      textAlign: "left",
                      marginBottom: "16px",
                    }}
                  >
                    {enumStrings.editMemberForm.featuredMemberNotActivatedDesc}
                  </p>
                )}

                <FeaturedMemberBannerGuidance
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: isMobile ? "14px" : "18px",
                    lineHeight: isMobile ? "20px" : "28px",
                    color: "#000000",
                    textAlign: "left",
                    marginBottom: isMobile ? "16px" : "24px",
                  }}
                />

                {/* Banner Upload Grid */}
                <div
                  style={isMobile ? {
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                    marginTop: "20px",
                    marginBottom: "24px",
                    width: "100%",
                  } : {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "50px",
                    marginTop: "20px",
                    justifyContent: "center",
                    marginBottom: "24px",
                  }}
                >
                  {isFeaturedMember ? (
                    bannersData.map((banner, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          width: isMobile ? "100%" : "209px",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: isMobile ? "8px" : "16px",
                          minWidth: 0,
                        }}
                      >
                        <p
                          style={{
                            textAlign: "center",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            fontSize: isMobile ? "15px" : "20px",
                            lineHeight: isMobile ? "20px" : "24px",
                            margin: 0,
                          }}
                        >
                          {enumStrings.editMemberForm.bannerLabel} {index + 1}
                        </p>
                        {banner.image || banner.videoUrl ? (
                          <div
                            style={{
                              backgroundColor: "#FFFFFF",
                              border: `2px solid ${theme.palette.primary.main}`,
                              borderRadius: "10px",
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                              width: isMobile ? "100%" : "209px",
                              height: "175px",
                              boxSizing: "border-box",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                position: "relative",
                                padding: "12px",
                                flex: 1,
                              }}
                            >
                              {banner.image ? (
                                <img
                                  src={banner.image}
                                  alt={`Banner ${index + 1}`}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: theme.palette.primary.main,
                                    fontSize: "14px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {enumStrings.common.video}
                                </div>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "6px 12px",
                              }}
                            >
                              <div style={{ display: "flex", gap: "8px" }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenBannerModal(index + 1, "PROFILE")}
                                  style={{
                                    backgroundColor: "#E3E3E3",
                                    borderRadius: "5px",
                                    width: "36px",
                                    height: "33px",
                                    padding: "4px",
                                  }}
                                >
                                  <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px", filter: "brightness(0)" }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteBanner(index)}
                                  style={{
                                    backgroundColor: (theme.palette as any).delete.main,
                                    borderRadius: "5px",
                                    width: "36px",
                                    height: "33px",
                                    padding: "4px",
                                  }}
                                >
                                  <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                                </IconButton>
                              </div>
                              <div style={{ display: "flex", gap: "4px" }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveBanner(index, 'left')}
                                  disabled={index === 0}
                                  style={{
                                    backgroundColor: index === 0 ? "#D9D9D9" : theme.palette.primary.main,
                                    color: index === 0 ? "#666" : "white",
                                    borderRadius: "5px",
                                    minWidth: "29px",
                                    width: "29px",
                                    height: "26px",
                                    padding: "4px",
                                  }}
                                >
                                  <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveBanner(index, 'right')}
                                  disabled={index === bannersData.length - 1}
                                  style={{
                                    backgroundColor: index === bannersData.length - 1 ? "#D9D9D9" : theme.palette.primary.main,
                                    color: index === bannersData.length - 1 ? "#666" : "white",
                                    borderRadius: "5px",
                                    minWidth: "29px",
                                    width: "29px",
                                    height: "26px",
                                    padding: "4px",
                                  }}
                                >
                                  <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                                </IconButton>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => handleOpenBannerModal(index + 1, "PROFILE")}
                            style={{
                              backgroundColor: "#FFFFFF",
                              border: `2px solid ${theme.palette.primary.main}`,
                              borderRadius: "10px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              width: isMobile ? "100%" : "182px",
                              height: isMobile ? "90px" : "114px",
                              boxSizing: "border-box",
                              cursor: "pointer",
                              padding: isMobile ? "12px 8px" : "22px 12px",
                              gap: isMobile ? "8px" : "10px",
                            }}
                          >
                            <img
                              src={UploadRoundIcon}
                              alt="Upload"
                              style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px" }}
                            />
                            <p
                              style={{
                                color: theme.palette.primary.main,
                                fontSize: isMobile ? "12px" : "20px",
                                fontWeight: 600,
                                margin: 0,
                                lineHeight: "1.3",
                                textAlign: "center",
                              }}
                            >
                              {enumStrings.editMemberForm.uploadBanner}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    [1, 2, 3, 4].map((num) => (
                      <div key={num} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "8px" : "16px", width: isMobile ? "100%" : "auto", minWidth: 0 }}>
                        <p style={{ textAlign: "center", fontWeight: 600, color: theme.palette.text.secondary, fontSize: isMobile ? "15px" : "20px", lineHeight: isMobile ? "20px" : "24px", margin: 0 }}>
                          {enumStrings.editMemberForm.bannerLabel} {num}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: isMobile ? "12px 8px" : "22px 12px",
                            gap: isMobile ? "8px" : "10px",
                            width: isMobile ? "100%" : "182px",
                            height: isMobile ? "90px" : "114px",
                            backgroundColor: theme.palette.text.secondary,
                            border: `2px solid ${theme.palette.text.secondary}`,
                            borderRadius: "10px",
                            boxSizing: "border-box",
                            cursor: "not-allowed",
                          }}
                        >
                          <img src={UploadRoundIcon} alt="Upload" style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px", filter: "brightness(0) invert(1)" }} />
                          <span style={{ fontWeight: 600, fontSize: isMobile ? "12px" : "20px", lineHeight: "1.3", textAlign: "center", color: "#FFFFFF" }}>
                            {enumStrings.editMemberForm.uploadBanner}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Preview & Update Banners Buttons */}
                <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? "10px" : "20px", flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (bannersData.some((b) => b.image || b.videoUrl)) {
                        setPreviewModalOpen(true);
                      }
                    }}
                    style={{
                      backgroundColor: bannersData.some((b) => b.image || b.videoUrl) ? "#000000" : theme.palette.text.secondary,
                      color: "white",
                      borderRadius: "12px",
                      padding: isMobile ? "12px 16px" : "15px 96px",
                      fontSize: isMobile ? "14px" : "20px",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "none",
                      flex: isMobile ? 1 : "0 0 auto",
                      minWidth: 0,
                      ...(!bannersData.some((b) => b.image || b.videoUrl) ? { cursor: "not-allowed" } : { cursor: "pointer" }),
                    }}
                  >
                    {enumStrings.addMemberBanners.previewBanners}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!isFeaturedMember || loader}
                    onClick={handleUpdateAllProfileBanners}
                    style={{
                      backgroundColor: isFeaturedMember ? theme.palette.primary.main : "#808080",
                      color: "white",
                      borderRadius: "12px",
                      padding: isMobile ? "12px 16px" : "15px 96px",
                      fontSize: isMobile ? "14px" : "20px",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "none",
                      flex: isMobile ? 1 : "0 0 auto",
                      minWidth: 0,
                      ...(!isFeaturedMember ? { cursor: "not-allowed", opacity: 0.5 } : { cursor: "pointer" }),
                    }}
                  >
                    {enumStrings.addMemberBanners.updateBannersButton}
                  </Button>
                </div>

              </div>

              {/* Perk Banners Section - Bordered Box */}
              <div
                style={{
                  width: "100%",
                  maxWidth: "1096px",
                  margin: isMobile ? "32px auto 0 auto" : "50px auto 0 auto",
                  padding: isMobile ? "0 0 20px 0" : "0px 30px 30px 30px",
                  border: isMobile ? "none" : `1px solid ${theme.palette.text.primary}`,
                  borderRadius: isMobile ? 0 : "16px",
                  position: "relative",
                  boxSizing: "border-box",
                }}
              >
                  {/* Green/Black Pill Header */}
                  <div
                    style={{
                      backgroundColor: isFeaturedMember ? theme.palette.success.main : "#000000",
                      borderRadius: "30px",
                      padding: isMobile ? "8px 14px" : "12px 32px",
                      display: "inline-block",
                      position: "relative",
                      top: isMobile ? "0" : "-24px",
                      left: "0",
                      marginBottom: isMobile ? "16px" : 0,
                      maxWidth: isMobile ? "calc(100% - 8px)" : undefined,
                    }}
                  >
                    <p
                      style={{
                        color: "#FFFFFF",
                        fontSize: isMobile ? "13px" : "20px",
                        fontWeight: 700,
                        margin: 0,
                        lineHeight: isMobile ? "16px" : "24px",
                      }}
                    >
                      {isFeaturedMember
                        ? enumStrings.editMemberForm.perkBannersActivatedTitle
                        : enumStrings.editMemberForm.perkBannersNotActivatedTitle}
                    </p>
                  </div>

                  {/* Description */}
                  {isFeaturedMember && hasPerks ? (
                    <p
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: isMobile ? "14px" : "18px",
                        lineHeight: isMobile ? "20px" : "28px",
                        color: "#000000",
                        fontWeight: 400,
                        textAlign: "left",
                        margin: "0 0 8px 0",
                      }}
                    >
                      {enumStrings.addMemberBanners.perkUploadDesc}
                    </p>
                  ) : isFeaturedMember && !hasPerks ? (
                    <p
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: isMobile ? "14px" : "18px",
                        lineHeight: isMobile ? "20px" : "28px",
                        color: "#000000",
                        fontWeight: 400,
                        textAlign: "left",
                        marginBottom: "16px",
                      }}
                    >
                      {enumStrings.addMemberBanners.noPerksDesc}
                    </p>
                  ) : (
                    <p
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: isMobile ? "14px" : "18px",
                        lineHeight: isMobile ? "20px" : "28px",
                        color: "#000000",
                        fontWeight: 400,
                        textAlign: "left",
                        marginBottom: "16px",
                      }}
                    >
                      {enumStrings.editMemberForm.perkBannersNotActivatedDesc}
                    </p>
                  )}

                  <FeaturedMemberBannerGuidance
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: isMobile ? "14px" : "18px",
                      lineHeight: isMobile ? "20px" : "28px",
                      color: "#000000",
                      fontWeight: 400,
                      textAlign: "left",
                      margin: isMobile ? "0 0 16px 0" : "0 0 24px 0",
                    }}
                  />

                  {/* List of Perks - only show if has perks */}
                  {hasPerks && (
                  <>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: isMobile ? "18px" : "32px",
                      lineHeight: isMobile ? "22px" : "24px",
                      color: isFeaturedMember ? theme.palette.primary.main : theme.palette.text.secondary,
                      margin: isMobile ? "0 0 16px 0" : "0 0 24px 0",
                    }}
                  >
                    {enumStrings.addMemberBanners.listOfPerks}
                  </p>

                  {/* Radio Button List of Perks */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "25px",
                      marginBottom: "24px",
                    }}
                  >
                    {memberPerks.map((perk: any, index: number) => (
                      <div
                        key={perk.id}
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "15px",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedPerkId(perk.id === selectedPerkId ? "" : perk.id)}
                      >
                        {/* Radio Circle */}
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "32px",
                            border: "1px solid #CCCCCC",
                            background: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {selectedPerkId === perk.id && (
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "32px",
                                background: theme.palette.primary.main,
                              }}
                            />
                          )}
                        </div>
                        {/* Perk Label */}
                        <span
                          style={{
                            fontFamily: "Noto Sans, sans-serif",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            color: "#000000",
                          }}
                        >
                          {enumStrings.addMemberBanners.perkLabel} {index + 1}  <strong>{perk.title}</strong>
                        </span>
                      </div>
                    ))}
                  </div>

                  {isFeaturedMember && selectedPerkId && (
                    <>
                      {/* Divider */}
                      <div
                        style={{
                          border: "1px solid #C4C4C4",
                          margin: "16px 0 24px 0",
                        }}
                      />

                      {/* Perk Title Banner Label */}
                      <p
                        style={{
                          fontWeight: 400,
                          fontSize: isMobile ? "16px" : "30px",
                          lineHeight: isMobile ? "22px" : "32px",
                          textAlign: "center",
                          color: "#000000",
                          margin: isMobile ? "0 0 20px 0" : "0 0 32px 0",
                        }}
                      >
                        {enumStrings.addMemberBanners.featuredMemberPerkBanner} {(() => {
                          const perkIndex = memberPerks.findIndex((p: any) => p.id === selectedPerkId);
                          return perkIndex + 1;
                        })()} {enumStrings.addMemberBanners.banner}
                        <br />
                        <strong>
                          {(() => {
                            const perk = memberPerks.find((p: any) => p.id === selectedPerkId);
                            return perk?.title || "";
                          })()}
                        </strong>
                      </p>

                      {/* Banner Upload Grid */}
                      <div
                        style={isMobile ? {
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px",
                          marginBottom: "32px",
                          width: "100%",
                        } : {
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "50px",
                          justifyContent: "center",
                          marginBottom: "32px",
                        }}
                      >
                        {perkBannersData.map((banner, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              width: isMobile ? "100%" : "182px",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: isMobile ? "8px" : "16px",
                              minWidth: 0,
                            }}
                          >
                            <p
                              style={{
                                textAlign: "center",
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                fontSize: isMobile ? "15px" : "20px",
                                lineHeight: isMobile ? "20px" : "24px",
                                margin: 0,
                              }}
                            >
                              {enumStrings.editMemberForm.bannerLabel} {index + 1}
                            </p>
                            {banner.image || banner.videoUrl ? (
                              <div
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  border: `2px solid ${theme.palette.primary.main}`,
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  display: "flex",
                                  flexDirection: "column",
                                  width: isMobile ? "100%" : "182px",
                                  height: "175px",
                                  boxSizing: "border-box",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    position: "relative",
                                    padding: "12px",
                                    flex: 1,
                                  }}
                                >
                                  {banner.image ? (
                                    <img
                                      src={banner.image}
                                      alt={`Perk Banner ${index + 1}`}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: theme.palette.primary.main,
                                        fontSize: "14px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {enumStrings.common.video}
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "6px 12px",
                                  }}
                                >
                                  <div style={{ display: "flex", gap: "8px" }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenBannerModal(index + 1, "PERK")}
                                      style={{
                                        backgroundColor: "#E3E3E3",
                                        borderRadius: "5px",
                                        width: "36px",
                                        height: "33px",
                                        padding: "4px",
                                      }}
                                    >
                                      <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px", filter: "brightness(0)" }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeletePerkBanner(index)}
                                      style={{
                                        backgroundColor: (theme.palette as any).delete.main,
                                        borderRadius: "5px",
                                        width: "36px",
                                        height: "33px",
                                        padding: "4px",
                                      }}
                                    >
                                      <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                                    </IconButton>
                                  </div>
                                  <div style={{ display: "flex", gap: "4px" }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMovePerkBanner(index, 'left')}
                                      disabled={index === 0}
                                      style={{
                                        backgroundColor: index === 0 ? "#D9D9D9" : theme.palette.primary.main,
                                        color: index === 0 ? "#666" : "white",
                                        borderRadius: "5px",
                                        minWidth: "29px",
                                        width: "29px",
                                        height: "26px",
                                        padding: "4px",
                                      }}
                                    >
                                      <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMovePerkBanner(index, 'right')}
                                      disabled={index === perkBannersData.length - 1}
                                      style={{
                                        backgroundColor: index === perkBannersData.length - 1 ? "#D9D9D9" : theme.palette.primary.main,
                                        color: index === perkBannersData.length - 1 ? "#666" : "white",
                                        borderRadius: "5px",
                                        minWidth: "29px",
                                        width: "29px",
                                        height: "26px",
                                        padding: "4px",
                                      }}
                                    >
                                      <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                                    </IconButton>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => handleOpenBannerModal(index + 1, "PERK")}
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  border: `2px solid ${theme.palette.primary.main}`,
                                  borderRadius: "10px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: isMobile ? "100%" : "182px",
                                  height: isMobile ? "90px" : "114px",
                                  boxSizing: "border-box",
                                  cursor: "pointer",
                                  padding: isMobile ? "12px 8px" : "22px 12px",
                                  gap: isMobile ? "8px" : "10px",
                                }}
                              >
                                <img
                                  src={UploadRoundIcon}
                                  alt="Upload"
                                  style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px" }}
                                />
                                <p
                                  style={{
                                    color: theme.palette.primary.main,
                                    fontSize: isMobile ? "12px" : "20px",
                                    fontWeight: 600,
                                    margin: 0,
                                    lineHeight: "1.3",
                                    textAlign: "center",
                                  }}
                                >
                                  {enumStrings.editMemberForm.uploadBanner}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  </>
                  )}

                  {/* Disabled Perk Banner Boxes when not featured */}
                  {!isFeaturedMember && (
                    <div
                      style={isMobile ? {
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                        marginTop: "20px",
                        marginBottom: "24px",
                        width: "100%",
                      } : {
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "50px",
                        marginTop: "20px",
                        justifyContent: "center",
                        marginBottom: "24px",
                      }}
                    >
                      {[1, 2, 3, 4].map((num) => (
                        <div key={num} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "8px" : "16px", width: isMobile ? "100%" : "auto", minWidth: 0 }}>
                          <p style={{ textAlign: "center", fontWeight: 600, color: theme.palette.text.secondary, fontSize: isMobile ? "15px" : "20px", lineHeight: isMobile ? "20px" : "24px", margin: 0 }}>
                            {enumStrings.editMemberForm.bannerLabel} {num}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              padding: isMobile ? "12px 8px" : "22px 12px",
                              gap: isMobile ? "8px" : "10px",
                              width: isMobile ? "100%" : "182px",
                              height: isMobile ? "90px" : "114px",
                              backgroundColor: theme.palette.text.secondary,
                              border: `2px solid ${theme.palette.text.secondary}`,
                              borderRadius: "10px",
                              boxSizing: "border-box",
                              cursor: "not-allowed",
                            }}
                          >
                            <img src={UploadRoundIcon} alt="Upload" style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px", filter: "brightness(0) invert(1)" }} />
                            <span style={{ fontWeight: 600, fontSize: isMobile ? "12px" : "20px", lineHeight: "1.3", textAlign: "center", color: "#FFFFFF" }}>
                              {enumStrings.editMemberForm.uploadBanner}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Preview & Update Banners Buttons - only show when featured member with perks */}
                  {isFeaturedMember && hasPerks && (
                  <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? "10px" : "20px", marginTop: "16px", flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (perkBannersData.some((b) => b.image || b.videoUrl)) {
                          setPreviewPerkModalOpen(true);
                        }
                      }}
                      style={{
                        backgroundColor: perkBannersData.some((b) => b.image || b.videoUrl) ? "#000000" : theme.palette.text.secondary,
                        color: "white",
                        borderRadius: "12px",
                        padding: isMobile ? "12px 16px" : "15px 96px",
                        fontSize: isMobile ? "14px" : "20px",
                        fontWeight: 700,
                        textTransform: "none",
                        boxShadow: "none",
                        flex: isMobile ? 1 : "0 0 auto",
                        minWidth: 0,
                        ...(!perkBannersData.some((b) => b.image || b.videoUrl) ? { cursor: "not-allowed" } : { cursor: "pointer" }),
                      }}
                    >
                      {enumStrings.addMemberBanners.previewBanners}
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!selectedPerkId || loader}
                      onClick={handleUpdateAllPerkBanners}
                      style={{
                        backgroundColor: selectedPerkId ? theme.palette.primary.main : "#808080",
                        color: "#FFFFFF",
                        borderRadius: "12px",
                        padding: isMobile ? "12px 16px" : "15px 96px",
                        fontSize: isMobile ? "14px" : "20px",
                        fontWeight: 700,
                        textTransform: "none",
                        boxShadow: "none",
                        flex: isMobile ? 1 : "0 0 auto",
                        minWidth: 0,
                        ...(!selectedPerkId ? { cursor: "not-allowed", opacity: 0.5 } : { cursor: "pointer" }),
                      }}
                    >
                      {enumStrings.addMemberBanners.updateBannersButton}
                    </Button>
                  </div>
                  )}
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Warning Modal */}
      <Modal
        open={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        className={classes.modal}
      >
        <div className={classes.modalPaper}>
          <button
            className={classes.closeButton}
            onClick={() => setShowWarningModal(false)}
          >
            ✕
          </button>
          <div style={{ marginBottom: "24px" }}>
            <WarningIcon style={{ fontSize: "64px", color: theme.palette.primary.main }} />
          </div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: 700,
              color: "#000000",
              margin: "0 0 16px 0",
            }}
          >
            {enumStrings.addMemberBanners.selectMemberFirst}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: 400,
              color: "#000000",
              margin: "0 0 32px 0",
            }}
          >
            {enumStrings.addMemberBanners.selectMemberDesc}
          </p>
          <Button
            variant="contained"
            onClick={() => setShowWarningModal(false)}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: "16px",
              fontWeight: 700,
              textTransform: "uppercase",
              padding: "12px 48px",
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            {enumStrings.common.okay}
          </Button>
        </div>
      </Modal>

      {/* Delete Banner Confirmation Modal */}
      <DeleteBannerModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={confirmDeleteBanner}
      />

      {/* Banner Upload Modal */}
      <BannerUploadModal
        open={bannerModalOpen}
        onClose={() => setBannerModalOpen(false)}
        bannerIndex={currentBannerIndex}
        useFeaturedMemberGuidance
        initialData={
          activeBannerType === "PROFILE"
            ? bannersData[currentBannerIndex - 1] || {}
            : perkBannersData[currentBannerIndex - 1] || {}
        }
        onSave={handleSaveBanner}
      />

      {/* Banner Preview Modal - Profile */}
      <BannerPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        banners={bannersData}
      />

      {/* Banner Preview Modal - Perks */}
      <BannerPreviewModal
        open={previewPerkModalOpen}
        onClose={() => setPreviewPerkModalOpen(false)}
        banners={perkBannersData}
        phoneMockupImage={PerksBannerPhoneMockup}
      />

      <SuccessDialog
        open={dialogState.open}
        onClose={() => setDialogState(prev => ({ ...prev, open: false }))}
        message={dialogState.message}
        variant={dialogState.variant}
        autoDismissMs={5000}
      />
      <ComingSoonDialog
        open={showComingSoon}
        onClose={() => setShowComingSoon(false)}
      />
    </div>
  );
};

export default AddMemberBanners;
